import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddProduct = () => {
  const [productData, setProductData] = useState({
    id: '',
    name: '',
    brand: '',
    price: '',
    img: '',
    description: '',
    quantity: 0,
    category: '',
    newCategory: '' // For the manual category input
  });
  const [categories, setCategories] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          'https://afric-fresh-backend.onrender.com/admin/categories', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };

    fetchCategories();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData({ ...productData, [name]: value });
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'africfreshupload');

    try {
      const response = await axios.post(
        'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
        formData
      );
      return response.data.secure_url;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Image upload failed. Please try again.');
      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const imageUrl = await handleImageUpload(file);
      setProductData({ ...productData, img: imageUrl });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Use the new category if it's filled, otherwise use the selected category
    const finalCategory = productData.newCategory.trim() 
      ? productData.newCategory 
      : productData.category;

    if (!finalCategory) {
      toast.error('Please select or enter a category');
      return;
    }

    try {
      const response = await axios.post(
        "https://afric-fresh-backend.onrender.com/admin/add-product",
        {
          ...productData,
          category: finalCategory // Send the determined category
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success(response.data.message);
      setProductData({
        id: '',
        name: '',
        brand: '',
        price: '',
        img: '',
        description: '',
        quantity: 0,
        category: '',
        newCategory: ''
      });
      setShowNewCategoryInput(false);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Something went wrong');
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white border border-gray-200 rounded-lg shadow-md">
      <ToastContainer />
      <form onSubmit={handleSubmit} className="space-y-4">
        {[ 
          { name: 'id', placeholder: 'ID', type: 'text', required: true },
          { name: 'name', placeholder: 'Name', type: 'text', required: true },
          { name: 'brand', placeholder: 'Brand', type: 'text' },
          { name: 'price', placeholder: 'Price', type: 'text', required: true },
          { name: 'quantity', placeholder: 'Quantity', type: 'number' },
        ].map((input) => (
          <div key={input.name}>
            <label htmlFor={input.name} className="block text-sm font-medium text-gray-700 mb-1">
              {input.placeholder}
            </label>
            <input
              type={input.type}
              name={input.name}
              value={productData[input.name]}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required={input.required || false}
            />
          </div>
        ))}
        
        {/* Image Upload Field */}
        <div>
          <label htmlFor="img" className="block text-sm font-medium text-gray-700 mb-1">
            Product Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isUploading}
          />
          {isUploading && <p className="text-sm text-gray-500 mt-1">Uploading image...</p>}
          {productData.img && !isUploading && (
            <div className="mt-2">
              <p className="text-sm text-green-600">Image uploaded successfully!</p>
              <img 
                src={productData.img} 
                alt="Preview" 
                className="mt-2 h-20 object-cover rounded"
              />
            </div>
          )}
        </div>

        {/* Category Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Category
          </label>
          
          {/* Existing Categories Dropdown */}
          <select
            name="category"
            value={productData.category}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={showNewCategoryInput}
          >
            <option value="">Select from existing categories</option>
            {categories.map((category) => (
              <option key={category._id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>

          {/* Toggle for new category */}
          <div className="flex items-center space-x-2">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="text-sm text-gray-500">OR</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          {/* New Category Option */}
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
              className={`px-3 py-2 rounded-md ${showNewCategoryInput ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700'}`}
            >
              {showNewCategoryInput ? '← Use existing' : '+ Add new category'}
            </button>
            
            {showNewCategoryInput && (
              <input
                type="text"
                name="newCategory"
                value={productData.newCategory}
                onChange={handleChange}
                placeholder="Enter new category name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={productData.description}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows="4"
            required
          ></textarea>
        </div>
        <button
          type="submit"
          className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : 'Add Product'}
        </button>
      </form>
    </div>
  );
};

export default AddProduct;